import axios from 'axios';

import {
  IPaymentMethod,
  TExchangeVgsTokenPayload,
  TExchangeVgsTokenResponse,
  TFeatureFlagApiResponse,
  TOktaApiResponse,
} from '../interfaces';

import destructureAxiosReq from './destructureAxiosReq';
import sureAxios from './sureAxios';
import { EEnvironment } from './useFeatureFlag';

export function setSDKAuthHeader(accessToken: string) {
  sureAxios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
}

export function deleteSDKAuthHeader() {
  delete sureAxios.defaults.headers.common.Authorization;
}

type TOktaApiOptions = {
  oktaIssuerUrl: string;
};

type TFeatureFlagOptions = {
  featureFlagUrl: string;
};

const api = {
  getFeatureFlag(
    {
      division,
      vertical,
      environment,
    }: {
      division: string;
      vertical: string;
      environment: EEnvironment;
    },
    opts: TFeatureFlagOptions
  ) {
    return destructureAxiosReq<TFeatureFlagApiResponse>(
      axios.get(
        `${opts.featureFlagUrl}${environment}/flags/${division}/${vertical}`
      )
    );
  },

  oktaAuthenticate({
    body,
    opts,
  }: {
    body: {
      username: string;
      password: string;
      options: Record<string, any>;
      context?: Record<string, any>;
    };
    opts: TOktaApiOptions;
  }) {
    return destructureAxiosReq<TOktaApiResponse>(
      axios.post(`${opts.oktaIssuerUrl}/api/v1/authn`, body)
    );
  },

  oktaResetPassword({
    body,
    opts,
  }: {
    body: {
      username: string;
      factorType: string;
      context?: Record<string, any>;
    };
    opts: TOktaApiOptions;
  }) {
    return destructureAxiosReq<TOktaApiResponse>(
      axios.post(`${opts.oktaIssuerUrl}/api/v1/authn/recovery/password`, body)
    );
  },

  oktaValidateRecoveryToken({
    body,
    opts,
  }: {
    body: {
      recoveryToken: string;
      context?: Record<string, any>;
    };
    opts: TOktaApiOptions;
  }) {
    return destructureAxiosReq<TOktaApiResponse>(
      axios.post(`${opts.oktaIssuerUrl}/api/v1/authn/recovery/token`, body)
    );
  },

  oktaPostRequest({
    url,
    body = {},
  }: {
    url: string;
    body: Record<string, any>;
  }) {
    return destructureAxiosReq<TOktaApiResponse>(axios.post(url, body));
  },

  changePassword(newPassword: string, oldPassword: string) {
    return destructureAxiosReq(
      sureAxios.post(`/api/user/v1/change-password`, {
        old_password: oldPassword,
        new_password: newPassword,
      })
    );
  },

  createPaymentMethod(body: {
    token?: string;
    email: string;
    name: string;
    gatewayReferenceId?: string;
    paymentServiceId?: string;
  }) {
    const payload: {
      source_token?: string;
      email: string;
      name: string;
      gateway_reference_id?: string;
      payment_service_id?: string;
    } = {
      email: body.email,
      name: body.name,
    };
    if (body.gatewayReferenceId) {
      payload.gateway_reference_id = body.gatewayReferenceId;
      payload.payment_service_id = body.paymentServiceId;
    }
    if (body.token) {
      payload.source_token = body.token;
    }
    return destructureAxiosReq<IPaymentMethod>(
      sureAxios.post(`/api/management/v1/payment_methods`, payload)
    );
  },

  exchangeVgsToken(opts: TExchangeVgsTokenPayload & { apiUrl: string }) {
    return destructureAxiosReq<TExchangeVgsTokenResponse>(
      axios.post(
        opts.apiUrl,
        {
          payment_method: {
            type: opts.type,
            number: opts.cardNumberToken,
            exp_month: opts.expMonth,
            exp_year: opts.expYear,
            cvc: opts.cvcToken,
          },
          carrier: {
            code: opts.carrierCode,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${opts.paymentApiAuthToken}`,
          },
        }
      )
    );
  },
};

export default api;
